import React, { Fragment, useState, useEffect, useCallback } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import {
    Container,
    Card,
    CardBody,
    Button,
    Label,
    Input,
    Spinner,
} from "reactstrap";
import { ToastContainer, toast } from "react-toastify";
import Breadcrumbs from "../../Components/Common/Breadcrumb";
import AxiosInstance from "../../services/AxiosInstance";
import "react-toastify/dist/ReactToastify.css";

const getModuleOperations = (mod) => {
    const str = mod.operation || "access";
    const arr = String(str).split("|").map((s) => s.trim()).filter(Boolean);
    return arr.length ? arr : ["access"];
};

const matrixToPermissionsFromMatrix = (matrix) => {
    const out = [];
    Object.keys(matrix || {}).forEach((moduleId) => {
        const ops = Object.keys(matrix[moduleId] || {}).filter((op) => matrix[moduleId][op]);
        if (ops.length) out.push({ module: moduleId, operations: ops });
    });
    return out;
};

const formatPermissionLabel = (op) =>
    op.replace(/\b\w/g, (c) => c.toUpperCase()).replace(/_/g, " ");

const RolePermissionsPage = () => {
    const { roleId } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const roleTitleFromState = location.state?.roleTitle;

    const [roleTitle, setRoleTitle] = useState(roleTitleFromState || "");
    const [modules, setModules] = useState([]);
    const [matrix, setMatrix] = useState({});
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [saving, setSaving] = useState(false);

    const buildMatrixFromPermissions = useCallback((perms, mods) => {
        const byMod = {};
        (perms || []).forEach((p) => {
            const mod = String(p.module ?? p.module_id ?? "");
            if (!byMod[mod]) byMod[mod] = { operations: [] };
            byMod[mod].operations.push(p.operation);
        });
        const next = {};
        (mods || []).forEach((mod) => {
            const modId = String(mod.module_id);
            const allowedOps = getModuleOperations(mod);
            next[modId] = {};
            allowedOps.forEach((op) => {
                next[modId][op] = !!(byMod[modId] && byMod[modId].operations.includes(op));
            });
        });
        return next;
    }, []);

    useEffect(() => {
        const id = parseInt(roleId, 10);
        if (!id) {
            setError("Invalid role");
            setLoading(false);
            return;
        }

        let cancelled = false;
        const load = async () => {
            setLoading(true);
            setError(null);
            try {
                const [rolesRes, modulesRes, permsRes] = await Promise.all([
                    AxiosInstance().post("/admin/role/get_roles", {}),
                    AxiosInstance().post("/admin/role/get_modules", {}),
                    AxiosInstance().post(`/admin/role/get_permissions/${id}`, {}),
                ]);

                if (cancelled) return;

                if (!modulesRes.data.status || !modulesRes.data.data) {
                    setError(modulesRes.data?.message || "Failed to load modules");
                    setLoading(false);
                    return;
                }
                const mods = modulesRes.data.data;
                setModules(mods);

                if (!roleTitleFromState && rolesRes.data.status && rolesRes.data.data) {
                    const role = rolesRes.data.data.find((r) => Number(r.user_role_id) === id);
                    if (role) setRoleTitle(role.user_role_title || "");
                } else if (roleTitleFromState) {
                    setRoleTitle(roleTitleFromState);
                }

                const perms = permsRes.data.status ? permsRes.data.data : [];
                setMatrix(buildMatrixFromPermissions(perms, mods));
            } catch (e) {
                if (!cancelled) {
                    const msg = e.response?.data?.message || (e.response?.status === 403 ? "Permission denied." : "Failed to load data");
                    setError(msg);
                    toast.error(msg);
                }
            } finally {
                if (!cancelled) setLoading(false);
            }
        };
        load();
        return () => { cancelled = true; };
    }, [roleId, roleTitleFromState, buildMatrixFromPermissions]);

    const handleChange = (moduleId, operation, checked) => {
        setMatrix((prev) => {
            const next = { ...prev };
            if (!next[moduleId]) next[moduleId] = {};
            next[moduleId] = { ...next[moduleId], [operation]: checked };
            return next;
        });
    };

    const handleSave = async () => {
        const id = parseInt(roleId, 10);
        if (!id) return;
        setSaving(true);
        try {
            const permissions = matrixToPermissionsFromMatrix(matrix);
            const res = await AxiosInstance().post("/admin/role/update", {
                user_role_id: id,
                role_name: roleTitle || "",
                permissions,
            });
            if (res.data.status) {
                toast.success(res.data.message || "Permissions updated");
                navigate("/job/admin/manage-roles");
            } else {
                toast.error(res.data.message || "Update failed");
            }
        } catch (e) {
            toast.error(e.response?.data?.message || "Request failed");
        } finally {
            setSaving(false);
        }
    };

    const goBack = () => navigate("/job/admin/manage-roles");

    if (loading && !modules.length) {
        return (
            <Fragment>
                <Breadcrumbs title="User Permissions" mainTitle="Roles & Permissions" />
                <div className="d-flex justify-content-center align-items-center py-5">
                    <Spinner color="primary" />
                </div>
            </Fragment>
        );
    }

    if (error) {
        return (
            <Fragment>
                <Breadcrumbs title="User Permissions" mainTitle="Roles & Permissions" />
                <Container fluid className="p-4">
                    <Card className="border-0 shadow-sm">
                        <CardBody className="text-center py-5">
                            <p className="text-danger mb-0">{error}</p>
                            <Button color="primary" className="mt-3" onClick={goBack}>
                                Back to Roles
                            </Button>
                        </CardBody>
                    </Card>
                </Container>
            </Fragment>
        );
    }

    return (
        <Fragment>
            <Breadcrumbs title="User Permissions" mainTitle="Roles & Permissions" />
            <Container fluid className="p-4">
                <div className="d-flex align-items-center gap-2 mb-4">
                    <Button color="light" size="sm" onClick={goBack}>
                        <i className="fa fa-arrow-left me-1" /> Back
                    </Button>
                </div>
                <h5 className="mb-2 fw-bold">User Permissions</h5>
                <h6 className="mb-4 text-muted">Permission Access: {String(roleTitle || "").toUpperCase() || roleId}</h6>

                {(!modules || modules.length === 0) ? (
                    <p className="text-muted mb-4">No modules defined. Add modules via Manage Modules.</p>
                ) : (
                    <div className="d-flex flex-column gap-4 mb-4">
                        {modules.map((mod) => {
                            const modId = String(mod.module_id);
                            const allowedOps = getModuleOperations(mod);
                            const row = matrix[modId] || {};
                            return (
                                <Card key={modId} className="border shadow-none mb-0">
                                    <CardBody className="py-3 px-4">
                                        <h6 className="mb-3 fw-semibold">{mod.module_name || modId}</h6>
                                        <div className="row g-3">
                                            {allowedOps.map((op) => (
                                                <div key={op} className="col-12 col-sm-6 col-md-4">
                                                    <div className="form-check form-switch">
                                                        <Input
                                                            type="switch"
                                                            className="form-check-input"
                                                            role="switch"
                                                            id={`perm-${modId}-${op}`}
                                                            checked={!!row[op]}
                                                            onChange={(e) => handleChange(modId, op, e.target.checked)}
                                                        />
                                                        <Label className="form-check-label small text-capitalize" htmlFor={`perm-${modId}-${op}`}>
                                                            {formatPermissionLabel(op)}
                                                        </Label>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </CardBody>
                                </Card>
                            );
                        })}
                    </div>
                )}

                <div className="d-flex justify-content-end gap-3 mt-4 pt-4 border-top">
                    <Button type="button" color="secondary" onClick={goBack}>Cancel</Button>
                    <Button type="button" color="primary" onClick={handleSave} disabled={saving}>
                        {saving ? "Saving..." : "Save Permissions"}
                    </Button>
                </div>
            </Container>
            <ToastContainer />
        </Fragment>
    );
};

export default RolePermissionsPage;
